import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { FilterChoosePeriod } from "@/components/filter/FilterChoosePeriod.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { ReportingBarTable } from "@/pages/reporting/components/ReportingBarTable.tsx";
import { RecruitingClosingTimeReportHiredTable } from "@/pages/reporting/recruiting/closing-time/RecruitingClosingTimeReportHiredTable.tsx";
import { RecruitingReportingRecruitersSelector } from "@/pages/reporting/recruiting/components/RecruitingReportingRecruitersSelector.tsx";
import { useRecruitingReports } from "@/pages/reporting/recruiting/recruiting-reports.hook.ts";
import { useRecruitAnalyticsClosingTimeReport } from "@/queries/analytics/use-recruit-analytics.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useStages } from "@/queries/recruit/use-stages.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiAnalyticsRecruitingFilter } from "@/types/analytics/analytics.recruiting.types.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { PipelineStageTypes } from "@/types/recruit/stages.types.ts";
import { ApiPermission, PermissionsKeys, PermissionType } from "@/types/role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Multiselect,
  Page,
  SelectItem,
  Skeleton,
  Stack,
  TabButton,
  TabList,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

// eslint-disable-next-line react-refresh/only-export-components
export const buildRecruitingClosingTimeSchema = (requiredError: string) =>
  z.object({
    stage_ids: z.array(z.number({ error: requiredError }), { error: requiredError }),
  });

export const RecruitingClosingTimeReport = () => {
  const { t } = useTranslation();

  const [section, setSection] = useState("average_time");
  useTitle(t("ats:report_closing_time_title"), t("common:reports"));

  const { data: employeeMe } = useEmployeeMe();

  function canIView(key: PermissionsKeys) {
    if (!employeeMe) return false;

    return (
      employeeMe.api_permissions[key] === PermissionType.view ||
      employeeMe.api_permissions[key] == PermissionType.edit
    );
  }

  const canViewStages = canIView(ApiPermission.recruiting_module);
  const { data: stages, isLoading: isLoadingStages } = useStages(canViewStages);
  const optionsStages = useMemo(() => {
    return (stages || []).filter(
      (stage) => stage.type !== PipelineStageTypes.hired
    ) as SelectItem[];
  }, [stages]);

  const schemaFields = buildRecruitingClosingTimeSchema(t("errors:field_required"));

  const {
    control,
    formState: { errors },
    watch,
  } = useForm<z.infer<typeof schemaFields>>({
    mode: "onChange",
    resolver: zodResolver(schemaFields),
    defaultValues: {
      stage_ids: [],
    },
  });

  const stageIds = watch("stage_ids");

  const renderFilterStages = () => {
    if (!canViewStages) return null;

    return (
      <Controller
        key={"stage_ids"}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("ats:stages")}
            options={optionsStages}
            values={value}
            onChange={onChange}
            loadOptionsOnOpen
            isClearable
            error={errors.stage_ids?.message}
            isLoading={isLoadingStages}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={"stage_ids"}
      />
    );
  };

  const { handlePreviewApplicants } = useRecruitingReports();

  const [, setSearchParams] = useSearchParams();
  const library = filtersLibrary();
  const {
    filter: { filter },
    config,
    filterButton,
  } = useAppFilter<ApiAnalyticsRecruitingFilter>({
    filters: [
      {
        ...library.period,
        defaultValue: "last_month_1",
      },
      {
        ...library.date_from,
        hideInFilter: true,
        name: "date_start",
      },
      {
        ...library.date_to,
        hideInFilter: true,
        name: "date_end",
      },
      {
        ...library.employee_ids,
        name: "recruiter_ids",
        label: t("ats:recruiters"),
      },
      library.job_ids,
      {
        ...library.employee_ids,
        name: "stakeholder_ids",
        label: t("ats:stakeholders"),
      },
    ],
    source: AnalyticReportTypes.recruitingCloseTime,
  });

  const filterWithStages = useMemo(() => {
    return {
      ...filter,
      ...(stageIds.length > 0 && { stage_ids: stageIds }),
    };
  }, [filter, stageIds]);

  const { data: report, isLoading } = useRecruitAnalyticsClosingTimeReport(filter);

  const { data: reportWithStages, isLoading: isLoadingWithStages } =
    useRecruitAnalyticsClosingTimeReport(filterWithStages);

  const renderCurrentSection = () => {
    if (!report) return null;

    if (section == "total_time") {
      return (
        <Box>
          <Box className="grid md:grid-cols-4">{renderFilterStages()}</Box>
          <Stack className={"py-12"}>
            {isLoadingWithStages ? (
              <Skeleton className="w-full h-10" />
            ) : reportWithStages ? (
              <ReportingBarTable
                rows={reportWithStages.report.total_days_rows.map((row) => ({
                  title: t(`ats:${row.total_days_range}`),
                  cells: [
                    {
                      value: row.applicants_ids.length,
                      title: t("plurals:plural_candidates", {
                        count: row.applicants_ids.length,
                      }),
                      entity_ids: row.applicants_ids,
                    },
                  ],
                }))}
                onClick={(_row, cell) => {
                  if (cell.entity_ids)
                    handlePreviewApplicants(
                      cell.entity_ids,
                      AnalyticReportTypes.recruitingCloseTime
                    );
                }}
              />
            ) : null}
          </Stack>
        </Box>
      );
    }

    return (
      <Box>
        <ReportingBarTable
          rows={report.report.avg_days_rows.map((row) => {
            return {
              title: row.pipeline_stage.title,
              cells: [
                {
                  value: row.avg_days,
                  title: t("plurals:plural_day", { count: Math.round(row.avg_days) }),
                  entity_ids: [],
                },
              ],
            };
          })}
        />
        <RecruitingClosingTimeReportHiredTable
          analytics_filter={filter}
          hired_applicants_ids={report.report.hired_applicants_ids}
        />
      </Box>
    );
  };

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.reporting.viewed, {
      [analyticProperties.title]: AnalyticReportTypes.recruitingCloseTime,
    });
  });

  return (
    <>
      <Box className={"bg-light h-full overflow-y-auto"}>
        <Page.Header title={t("ats:report_closing_time_title")}>
          <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
            <FilterChoosePeriod filtersConfig={config} />
            {filterButton()}
          </Stack>
        </Page.Header>

        <Page.Content>
          <Box>
            <RecruitingReportingRecruitersSelector
              recruiterIds={filter.recruiter_ids || []}
              onChange={(recruiterIds) => {
                setSearchParams((searchParams) => {
                  searchParams.set("recruiter_ids", recruiterIds.join(","));
                  return searchParams;
                });
              }}
            />
          </Box>
          {isLoading && <Skeleton className={"w-full h-10"} />}
          {report && (
            <Stack>
              <Box className={"grid md:grid-cols-3"}>
                <Stack className={twMerge("pt-10 pb-4")}>
                  <span className={"text-[40px] leading-[40px]  font-semibold"}>
                    {Math.round(report.report.average_days_for_closing).toString()}
                  </span>
                  <Text className={"text-lg text-secondary"}>{t("ats:avg_days_to_close")}</Text>
                </Stack>
                <Stack className={twMerge("pt-10 pb-4")}>
                  <Box>
                    <span className={"text-[40px] leading-[40px] font-semibold"}>
                      {" "}
                      {Math.round(report.report.hired_candidate_count).toString()}
                    </span>
                    <span className={"text-[24px] leading-[32px] text-secondary  font-semibold"}>
                      {" / "}
                      {Math.round(report.report.hired_from_jobs_count).toString()}
                    </span>
                  </Box>
                  <Text className={"text-lg text-secondary"}>{t("ats:jobs_closed_with_hire")}</Text>
                </Stack>
              </Box>
              <Title header={t("ats:time_on_stage")} size={"md"} paddingTop paddingBottom />
              <div className={"w-full overflow-hidden"}>
                <TabList>
                  <TabButton
                    title={t("ats:avg_time")}
                    isActive={section == "average_time"}
                    onClick={() => {
                      setSection("average_time");
                    }}
                  />
                  <TabButton
                    title={t("ats:total_time_on_current_stage")}
                    isActive={section == "total_time"}
                    onClick={() => {
                      setSection("total_time");
                    }}
                  />
                </TabList>
                <Stack className={"py-12"}>{renderCurrentSection()}</Stack>
              </div>
            </Stack>
          )}
        </Page.Content>
      </Box>
    </>
  );
};
